import { faroCoreApiClient } from "@api/faro-core-api-client";
import { AddressAutocomplete } from "@components/address-autocomplete/address-autocomplete";
import { STACK_ERROR_SX } from "@components/common/dialog/faro-dialog";
import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { FaroSelect } from "@components/common/faro-select/faro-select";
import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { GroupAutoComplete } from "@components/common/group-autocomplete/group-autocomplete";
import { LabelWithHelp } from "@components/common/label-with-help";
import {
  MemberAutocomplete,
  SelectedMember,
} from "@components/common/member-autocomplete/member-autocomplete";
import { getErrorDisplayMarkup } from "@context-providers/error-boundary/error-boundary-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { GroupId } from "@custom-types/base-types";
import { Button, Dialog } from "@flat-ui";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Alert, Box, FormHelperText, Stack } from "@mui/material";
import { useCompanyId } from "@context-providers/company-id-provider";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ConvertUtils } from "@stellar/utils";
import { appStateSelector } from "@store/app/app-selector";
import { addCreatedProject } from "@store/projects-map/projects-map-slice";
import { addProject } from "@store/projects/projects-slice";
import { convertProjectTypesToSdbProject } from "@store/projects/projects-slice-utils";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { FetchingStatus } from "@store/store-types";
import { formatAreaUnit } from "@utils/data-display";
import { GeocodedAddress } from "@utils/geocoding-utils";
import { validateNumber } from "@utils/math-utils";
import { parseCoordinatesString } from "@utils/string-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DASH } from "@utils/ui-utils";
import { useMemo, useState } from "react";

interface Errors {
  name: string | null;
  selectedGroupId: GroupId | null;
  selectedProjectManagerIdentity: string | null;
  area: string | null;
  clientName: string | null;
  projectDescription: string | null;
}

const DEFAULT_ERRORS: Errors = {
  name: null,
  selectedGroupId: null,
  selectedProjectManagerIdentity: null,
  area: null,
  clientName: null,
  projectDescription: null,
};

/** Defines the minimum area in sqft that we allow for the project area. */
const MINIMUM_PROJECT_AREA = 1;

interface Props {
  /** Whether the dialog is opened from the demo empty page */
  isDemo?: boolean;
}

/** Renders the create project button and the functionality in projects page */
export function CreateProjectDialog({
  isDemo = false,
}: Props): JSX.Element | null {
  const [selectedProjectManager, setSelectedProjectManager] =
    useState<SelectedMember>(null);
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();
  const { isScreenSmOrSmaller } = useMediaQueryList();
  const companyId = useCompanyId();
  const { trackEvent } = useTrackEvent();
  const { navigateToProjectDetail } = useAppNavigation();
  const { canCreateProjects } = useHasUserValidFeatureCompanyLevel();
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const lastPage = 1;
  const [groupMembers, setGroupMembers] = useState<
    FaroCoreApiTypes.GroupMember[]
  >([]);
  const [name, setName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [selectedGroupId, setSelectedGroupId] = useState<GroupId | undefined>(
    undefined
  );
  /** Stores the raw input of the area before validation and parsing. */
  const [area, setArea] = useState<string>("");
  /** Stores the error message of the area if there is one, null otherwise. */
  const [areaErrorMessage, setAreaErrorMessage] = useState<string | null>(null);
  /** Stores the parsed version of the area, only updates when valid values are input */
  const [areaParsed, setAreaParsed] = useState<number>(MINIMUM_PROJECT_AREA);
  const [areaUnit, setAreaUnit] = useState<FaroCoreApiTypes.AreaUnit>("sqft");
  const [clientName, setClientName] = useState<string>("");
  const [projectDescription, setProjectDescription] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [position, setPosition] = useState<
    FaroCoreApiTypes.Position | undefined
  >(undefined);
  const [geocodingAddressState, setGeocodingAddressState] =
    useState<FetchingStatus>("uninitialized");
  const isGeocodingAddress = geocodingAddressState === "pending";

  /** Gets the identity of the selected project manager */
  const selectedProjectManagerIdentity: string | null = useMemo(() => {
    if (!selectedProjectManager) {
      return null;
    }
    if (typeof selectedProjectManager === "string") {
      return selectedProjectManager;
    }
    return selectedProjectManager?.identity;
  }, [selectedProjectManager]);

  const [errors, setErrors] = useState<Errors>(DEFAULT_ERRORS);

  /** Evaluates whether the project area text field has a valid input. */
  const isAreaValid = useMemo(() => {
    return areaErrorMessage === null;
  }, [areaErrorMessage]);

  /**
   * Evaluates whether the confirm button should be disabled because there are errors in the dialog.
   */
  const isConfirmDisabled: boolean = useMemo(() => {
    // The first page is never disabled because the fields are validated
    // when the user clicks on next.
    if (page === 0) {
      return false;
    }

    // For the second page, disable the confirm button if the input area value is invalid or
    // if the address is being geocoded, to prevent the user from creating the project before
    // the address GPS position is obtained.
    return !isAreaValid || isGeocodingAddress;
  }, [page, isAreaValid, isGeocodingAddress]);

  /** Whether the back button should be shown in the dialog. */
  const shouldShowBackButton = page > 0 && lastPage > 0;

  // eslint-disable-next-line @typescript-eslint/require-await -- Please review lint error
  async function onOpenDialog(): Promise<void> {
    trackEvent({ name: ProjectEvents.startCreateNewProject });

    setName("");
    setAddress("");
    setSelectedGroupId(undefined);
    setSelectedProjectManager(null);
    setClientName("");
    setArea("");
    setAreaErrorMessage(null);
    setAreaParsed(MINIMUM_PROJECT_AREA);
    setAreaUnit("sqft");
    setProjectDescription("");
    setPage(0);
    setErrorMessage(null);
    setErrors(DEFAULT_ERRORS);
    setIsDialogOpen(true);
  }

  /**
   * Triggered every time the user inputs text in the project area
   * text fields. It sets the raw value of the area that is displayed on the text field
   * but it also stores the parsed value if it is valid or the error message if not.
   */
  function onAreaChanged(event: React.ChangeEvent<HTMLInputElement>): void {
    const changedArea = event.target.value;
    setArea(changedArea);
    const validatedArea = validateNumber({
      attrName: "Area",
      // Since area is optional we can allow the user to leave the field empty
      // and in that case we use the minimum value for the area.
      // what we don't allow is setting the value to 0.
      value: changedArea === "" ? 1 : changedArea,
      minOrEqual: 1,
      shouldAllowDecimal: false,
    });
    if (validatedArea.isValid) {
      setAreaErrorMessage(null);
      setAreaParsed(validatedArea.parsedNumber);
    } else {
      setAreaErrorMessage(validatedArea.errorMessage);
      setAreaParsed(MINIMUM_PROJECT_AREA);
    }
  }

  /**
   * Triggered every time a group is selected.
   * It fetches the members that belong to that group and stores them
   * in the members variable.
   *
   * @param groupId Id of the group to fetch its members from.
   * @returns The fetched members for the provided group, otherwise undefined.
   */
  async function onGroupSelected(
    groupId: GroupId | undefined
  ): Promise<FaroCoreApiTypes.GroupMember[] | undefined> {
    if (!groupId) {
      setSelectedGroupId(undefined);
      setSelectedProjectManager(null);

      return;
    }

    setSelectedGroupId(groupId);
    // As soon as you select a different group, empty the members array.
    // TODO: This should be moved to the group slice as createAsyncThunk: https://faro01.atlassian.net/browse/ST-698
    setGroupMembers([]);
    try {
      const { members } = await faroCoreApiClient.groups.getGroupById({
        companyId,
        groupId,
      });
      setGroupMembers(members ?? []);
      // If we change groups we don't want to have the project manager already selected, except if there's exactly 1 member.
      setSelectedProjectManager(members?.length === 1 ? members[0] : null);
      return members;
    } catch (error) {
      handleErrorWithToast({
        id: `getGroupById-${Date.now().toString()}`,
        title: "Cannot get the group details",
        error,
      });
    }
  }

  function setSingleError(
    errorKey: keyof typeof errors,
    errorValue: string | null
  ): void {
    setErrors((current) => {
      return {
        ...current,
        [errorKey]: errorValue,
      };
    });
  }

  /**
   * poor man's validation until we have a standardized validation technique
   *
   * @param pageNumber which page to validate
   * @returns a boolean that indicates whether the form is valid or not
   */
  function validateForm(pageNumber: number): boolean {
    // first clear all current errors
    setErrors(DEFAULT_ERRORS);

    // next, validate one by one
    let hasNoErrors = true;
    if (pageNumber === 0) {
      if (!name) {
        setSingleError("name", "Project name is required!");
        hasNoErrors = false;
      }
      if (!selectedGroupId) {
        setSingleError("selectedGroupId", "Project group is required!");
        hasNoErrors = false;
      }
      if (!selectedProjectManagerIdentity) {
        setSingleError(
          "selectedProjectManagerIdentity",
          "Project manager is required!"
        );
        hasNoErrors = false;
      }
    }

    // why do we need to manually set a variable "hasNoErrors" instead of checking the errors (by looping for example)?
    // because of react's queuing system. setState is not immediate, but we need to know if there is an error
    // immediately in the function that uses this function without using useEffect
    return hasNoErrors;
  }

  /**
   * Sends a request to the backend to create a project.
   * If the promise is resolved it means that the project was successfully created,
   * otherwise an error is thrown.
   */
  async function createProject(): Promise<void> {
    // Theoretically the user should not be able to reach this state
    // if the project manager was not provided but do this for type safety
    // and as an edge case if our members selection didn't work properly.
    if (!selectedProjectManagerIdentity) {
      throw new Error("Project manager is required!");
    }

    const sqft =
      areaUnit === "sqft"
        ? areaParsed
        : ConvertUtils.sqMeterToSqFoot(areaParsed);

    const project = await faroCoreApiClient.projects.createNewProject({
      companyId,
      payload: {
        name,
        // We allow the user to leave the address empty, but the backend requires an non-empty string,
        // so we use a DASH character as an accepted workaround to indicate that the address is empty.
        address: address.trim() || DASH,
        clientName,
        description: projectDescription,
        sqft,
        groupId: String(selectedGroupId),
        projectManagerIdentity: selectedProjectManagerIdentity,
        position,
      },
    });

    // Add SdbProject entity to both projects and map projects redux slices.
    const sdbProject = convertProjectTypesToSdbProject(project);
    dispatch(addProject(sdbProject));
    dispatch(addCreatedProject(sdbProject));

    navigateToProjectDetail({ companyId, projectId: project.id });
  }

  function trackConfirm(): void {
    if (page !== lastPage) {
      return;
    }

    const eventProps: Record<string, string | number> = {
      descriptionLength: projectDescription?.length ?? 0,
      projectNameLength: name.length,
      addressLength: address.length,
      clientNameLength: clientName.length,
      area: areaParsed ?? 0,
      isGpsPositionSet: position ? "yes" : "no",
    };

    trackEvent({
      name: ProjectEvents.createNewProject,
      props: eventProps,
    });
  }

  /** Submit groups to be invited */
  async function handleConfirm(): Promise<void> {
    trackConfirm();

    // If the form is not valid have an early exit
    if (!validateForm(page)) {
      return;
    }

    if (page < lastPage) {
      // If we are not in the last page, the confirm button is only used to go to the next page.
      setPage(page + 1);
      return;
    }

    setIsLoading(true);

    try {
      await createProject();
      // If the project has been created the dialog can be closed.
      setIsDialogOpen(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // If the project creation failed, do not close the dialog and
      // show an error message inside the dialog.
      const errorMessage = getErrorDisplayMarkup(error);
      setErrorMessage(errorMessage);
    } finally {
      // Regardless whether it succeeded or fail to create a project, the loading spinner
      // should be hidden and the user should be able to initiate a new request to create
      // a project or manually close the dialog
      setIsLoading(false);
    }
  }

  /**
   * Handles a change in the address input field.
   * If the new value is a valid coordinates string, it sets the position to
   * that value, otherwise it sets the position to undefined.
   * @param input The new address input value
   */
  function onAddressInputChange(input: string): void {
    setAddress(input);
    const trimmedInput = input.trim();
    const position = parseCoordinatesString(trimmedInput);
    setPosition(position ?? undefined);
  }

  /**
   * Handles the selection of a geocoded address. Sets both address and the position
   * @param geocodedAddress The selected geocoded address
   */
  function onGeocodedAddressSelect(geocodedAddress: GeocodedAddress): void {
    setAddress(geocodedAddress.address);
    setPosition(geocodedAddress.position);
  }

  /**
   * We should not render create project button
   * if the feature to create projects in company context isn't enabled and permitted
   * That means either the company does not have the feature to create projects
   * Or the current user has no permission to create projects.
   */
  if (!canCreateProjects) {
    return null;
  }

  return (
    <>
      {/* Button to open the dialog */}
      <Button
        onClick={onOpenDialog}
        sx={{ width: isScreenSmOrSmaller && !isDemo ? "100%" : "auto" }}
      >
        New project
      </Button>

      <Dialog
        title={page === 0 ? "Create new project" : "Project details"}
        open={isDialogOpen}
        confirmText={page === lastPage ? "Create" : "Next"}
        isConfirmDisabled={isConfirmDisabled}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        onClose={() => setIsDialogOpen(false)}
        onCancel={() => setIsDialogOpen(false)}
        secondaryText={shouldShowBackButton ? "Back" : undefined}
        onSecondary={shouldShowBackButton ? () => setPage(page - 1) : undefined}
        showXButton={false}
      >
        {/* First page: Required fields */}
        {page === 0 && (
          <>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Project name" isRequired />
              <FaroSimpleTextField
                value={name}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
                error={!!errors.name}
                helperText={errors.name}
                placeholder="Enter the project name..."
                size="small"
              />
            </Stack>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp
                title="Group"
                help={
                  "Groups are created to allow for a more flexible structure and ensure " +
                  "better management of members and projects."
                }
                isRequired
              />

              <GroupAutoComplete
                callbackOnGroupSelected={onGroupSelected}
                selectedGroupId={selectedGroupId}
              />

              {errors.selectedGroupId && (
                <FormHelperText error sx={{ ml: 2 }}>
                  {errors.selectedGroupId}
                </FormHelperText>
              )}
            </Stack>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp
                title="Project manager"
                help={
                  "Choose a project manager to manage this project. This person can edit " +
                  "permissions, invite new members and also can archive, download and remove " +
                  "their related projects."
                }
                isDisabled={!selectedGroupId}
                isRequired
              />
              <MemberAutocomplete
                members={groupMembers}
                selectedMember={selectedProjectManager}
                onOptionChange={setSelectedProjectManager}
                isDisabled={!selectedGroupId}
                placeholder="Choose who can manage this project..."
              />

              {errors.selectedProjectManagerIdentity && (
                <FormHelperText error sx={{ ml: 2 }}>
                  {errors.selectedProjectManagerIdentity}
                </FormHelperText>
              )}
            </Stack>
          </>
        )}

        {/* Second page: Optional fields */}
        {page === 1 && (
          <>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp
                title="Client name"
                help="The client is the person or organization for whom this project is carried out."
              />
              <FaroSimpleTextField
                autoFocus={true}
                value={clientName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setClientName(event.target.value);
                }}
                placeholder="Enter the client name..."
                size="small"
                error={!!errors.clientName}
                helperText={errors.clientName}
              />
            </Stack>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Address" />
              {/* TODO: Remove feature flag and only keep the AddressAutocomplete block -> https://faro01.atlassian.net/browse/ST-3944 */}
              {isAlphaTestingEnabled ? (
                <AddressAutocomplete
                  input={address}
                  setInput={onAddressInputChange}
                  shouldAutoFocus={false}
                  placeholder="Example: street, region, country"
                  onGeocodedAddressSelect={onGeocodedAddressSelect}
                  onLoadingGeocodedAddressChange={setGeocodingAddressState}
                />
              ) : (
                <FaroSimpleTextField
                  value={address}
                  autoFocus={false}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setAddress(event.target.value);
                  }}
                  placeholder="Example: street, region, country"
                  size="small"
                />
              )}
            </Stack>
            <Stack>
              <LabelWithHelp
                title="Project area"
                help={
                  "A project area is the amount of square feet/meter that is assigned " +
                  "to a project. This value must be equal to or greater than 1."
                }
              />
            </Stack>
            <Box
              component="div"
              sx={{
                ...STACK_ERROR_SX,
                display: "flex",
                alignItems: "top",
                width: "100%",
              }}
            >
              <Box
                component="div"
                sx={{
                  width: "100%",
                }}
              >
                <FaroSimpleTextField
                  value={area}
                  // Using type="number" brought some undesired behavior like not working
                  // properly on Firefox or showing the buttons to increase/decrease value
                  // that are useless in this case. Therefore we use our own validation.
                  type="text"
                  autoFocus={false}
                  onChange={onAreaChanged}
                  placeholder="Enter project area..."
                  size="small"
                  InputProps={{
                    inputProps: {
                      min: 0,
                    },
                    sx: {
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    },
                  }}
                  sx={{
                    width: "100%",
                  }}
                  error={!!errors.area || !isAreaValid}
                  helperText={errors.area || areaErrorMessage}
                  style={{ flexGrow: 1 }}
                />
              </Box>
              <Box component="div">
                <FaroSelect
                  value={areaUnit}
                  size="small"
                  variant="outlined"
                  isSplitInput={true}
                  onChange={setAreaUnit}
                >
                  <FaroMenuItem value="sqft">
                    {formatAreaUnit("sqft")}
                  </FaroMenuItem>
                  <FaroMenuItem value="sqm">
                    {formatAreaUnit("sqm")}
                  </FaroMenuItem>
                </FaroSelect>
              </Box>
            </Box>
            <Stack sx={STACK_ERROR_SX}>
              <LabelWithHelp title="Project description" />
              <FaroSimpleTextField
                value={projectDescription}
                multiline
                minRows={4}
                autoFocus={false}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setProjectDescription(event.target.value);
                }}
                placeholder="Enter project description..."
                size="small"
                error={!!errors.projectDescription}
                helperText={errors.projectDescription}
              />
            </Stack>
            {errorMessage && (
              <Stack sx={STACK_ERROR_SX}>
                <Alert severity="error">{errorMessage}</Alert>
              </Stack>
            )}
          </>
        )}
      </Dialog>
    </>
  );
}
